import PropTypes from 'prop-types';
import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Button,
    FormControl,
    FormHelperText,
    Grid,
    InputLabel,
    OutlinedInput,
    TextField,
    Typography,
    useMediaQuery,
    MenuItem
} from '@mui/material';

// third party
import * as Yup from 'yup';
import { Formik } from 'formik';

// project imports
import 'utils/YupHelper.js';
import { CustomInput, CustomSelect } from 'views/utilities/CustomComponent';
import { NextPayDateGreg, PersianDayToGregDay, GregDayToPersianDay } from 'utils/DateHelper';
import SnackAlert from 'views/utilities/SnackAlert';
import useScriptRef from 'hooks/useScriptRef';
import AnimateButton from 'ui-component/extended/AnimateButton';
import { AuthHeader, ApiUrl } from 'utils/Constants';
import { UserData } from 'utils/User';
import { FetchData, SetError } from 'utils/FetchData';

// ===========================|| FIREBASE - FUND ||=========================== //

const FirebaseFund = ({ onClose, ...others }) => {
    const theme = useTheme();
    const scriptedRef = useScriptRef();
    const matchDownSM = useMediaQuery(theme.breakpoints.down('md'));
    const ref = useRef();
    const [accounts, setAccounts] = useState([]);
    const [remainDays, setRemainDays] = useState([]);

    const { fundId } = useParams();

    const fieldDisplay = !fundId ? 'inherit' : 'none';

    useEffect(() => {
        const getFund = async () => {
            if (!fundId) return false;

            const res = await fetch(`${ApiUrl}/api/fund/${fundId}?count=1&details=1`, {
                method: 'GET',
                headers: AuthHeader
            });
            // console.log(res);
            const response = await res.json();
            const data = response.data;
            const qData = JSON.parse(data.data);
            setRemainDays(qData.remainDays);

            const newDate = NextPayDateGreg(qData.remainDays);
            const shamsiPeriod = data?.period_type == 'Month' ? GregDayToPersianDay(newDate, data?.period_day) : null;

            ref.current.setFieldValue('name', data?.name || '');
            ref.current.setFieldValue('membershipFee', data?.membership_fee || 0);
            ref.current.setFieldValue('periodType', data?.period_type || 'Month');
            ref.current.setFieldValue('periodDay', shamsiPeriod || data?.period_day || 1);
            ref.current.setFieldValue('account', data?.account || '');
        };

        const getAccounts = async () => {
            const userId = UserData()?.Id;
            if (!userId) {
                setAccounts([]);
                return false;
            }
            const res = await fetch(`${ApiUrl}/api/account?user=${userId}`, {
                method: 'GET',
                headers: AuthHeader
            });
            // console.log(res);
            const response = await res.json();
            setAccounts(response.data || []);
        };

        getFund();
        getAccounts();
    }, [fundId]);

    return (
        <>
            <Formik
                innerRef={ref}
                initialValues={{
                    fundType: 'Loan',
                    name: '',
                    membershipFee: '',
                    periodType: 'Month',
                    periodDay: 1,
                    account: ''
                }}
                validationSchema={Yup.object().shape({
                    fundType: Yup.mixed()
                        .oneOf(['Loan', 'Lottery', 'Building'], 'نوع صندوق را انتخاب نمایید')
                        .required('نوع صندوق الزامی است'),
                    name: Yup.string('عنوان الزامی است').required('عنوان الزامی است'),
                    membershipFee: Yup.string().moreThan(500, 'لطفاً یک عدد بیش از 500 وارد نمایید').required('حق عضویت الزامی است'),
                    periodType: Yup.mixed().oneOf(['Month', 'Week'], 'دوره‌ی پرداخت را انتخاب نمایید').required('دوره‌ی پرداخت الزامی است'),
                    periodDay: Yup.string()
                        .required('روز سررسید الزامی است')
                        .moreThan(1, 'لطفاً یک عدد وارد نمایید')
                        .when(['periodType'], {
                            is: 'Month',
                            then: Yup.string().lessThan(30, 'لطفاً یک عدد بین 1 تا 30 وارد نمایید'),
                            otherwise: Yup.string().lessThan(7, 'لطفاً یک عدد بین 1 تا 7 وارد نمایید')
                        }),
                    account: Yup.string()
                })}
                onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
                    try {
                        const newDate = fundId ? NextPayDateGreg(remainDays) : new Date();
                        const gregPeriod = values.periodType == 'Month' ? PersianDayToGregDay(newDate, values.periodDay) : null;

                        fetch(`${ApiUrl}/api/fund/` + (fundId || ''), {
                            method: !fundId ? 'POST' : 'PUT',
                            headers: AuthHeader,
                            body: JSON.stringify({
                                fund_type: values.fundType || 'Loan',
                                name: values.name,
                                membership_fee: values.membershipFee || 1000,
                                period_type: values.periodType || 'Month',
                                period_day: gregPeriod || values.periodDay || 1,
                                account: values.account
                            })
                        })
                            .then((res) => res.json())
                            .then(
                                (result) => {
                                    const data = FetchData(result, setStatus, setErrors);
                                    setSubmitting(false);
                                    if (result.success) {
                                        setTimeout(() => {
                                            onClose && onClose();
                                        }, 2000);
                                    }
                                },
                                (error) => {
                                    console.table(error);
                                    SetError(error, setStatus, setErrors);
                                    setSubmitting(false);
                                }
                            );
                    } catch (err) {
                        // console.error(err);
                        if (scriptedRef.current) {
                            SetError(err, setStatus, setErrors);
                            setSubmitting(false);
                        }
                    }
                }}
            >
                {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values, status }) => (
                    <form noValidate onSubmit={handleSubmit} {...others}>
                        <Grid container spacing={matchDownSM ? 0 : 1}>
                            <Grid item xs={12} sm={12} sx={{ display: fieldDisplay }}>
                                <CustomSelect
                                    name="fundType"
                                    label="نوع صندوق"
                                    value={values.fundType}
                                    error={touched.fundType && errors.fundType}
                                    onChange={handleChange}
                                >
                                    <MenuItem value="Loan" key="0">
                                        قرض الحسنه - پس انداز
                                    </MenuItem>
                                    <MenuItem value="Lottery" key="1">
                                        ساختمانی
                                    </MenuItem>
                                </CustomSelect>
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <CustomInput
                                    type="text"
                                    name="name"
                                    label="عنوان"
                                    value={values.name}
                                    error={touched.name && errors.name}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <CustomInput
                                    type="text"
                                    name="membershipFee"
                                    label="حق عضویت"
                                    value={values.membershipFee}
                                    commaInput={true}
                                    error={touched.membershipFee && errors.membershipFee}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={6} sm={6}>
                                <CustomSelect
                                    name="periodType"
                                    label="دوره‌ی پرداخت"
                                    value={values.periodType}
                                    error={touched.periodType && errors.periodType}
                                    onChange={handleChange}
                                >
                                    <MenuItem value="Month" key="0">
                                        ماهانه
                                    </MenuItem>
                                    <MenuItem value="Week" key="1">
                                        هفتگی
                                    </MenuItem>
                                </CustomSelect>
                            </Grid>
                            <Grid item xs={6} sm={6}>
                                <CustomInput
                                    type="number"
                                    name="periodDay"
                                    label="روزِ سررسید"
                                    value={values.periodDay}
                                    error={touched.periodDay && errors.periodDay}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    min={1}
                                    max={values.periodType == 'Month' ? 30 : 7}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <CustomSelect
                                    name="account"
                                    label="حساب مدیر"
                                    value={values.account}
                                    error={touched.account && errors.account}
                                    onChange={handleChange}
                                >
                                    {accounts.map((account) => (
                                        <MenuItem value={account.id} key={account.id}>
                                            {account.account_number} {account.bank_name}
                                        </MenuItem>
                                    ))}
                                </CustomSelect>
                            </Grid>
                        </Grid>

                        {errors.submit && <SnackAlert message={errors.submit} type="error" />}

                        {status && status.success && <SnackAlert fieldName="صندوق" fieldEdited={fundId > 0} type="success" />}

                        <Box sx={{ mt: 2 }}>
                            <AnimateButton>
                                <Button
                                    disableElevation
                                    disabled={isSubmitting}
                                    fullWidth
                                    size="large"
                                    type="submit"
                                    variant="contained"
                                    color="secondary"
                                >
                                    ثبت اطلاعات
                                </Button>
                            </AnimateButton>
                        </Box>
                    </form>
                )}
            </Formik>
        </>
    );
};

FirebaseFund.propTypes = {
    onClose: PropTypes.func
};

export default FirebaseFund;
